import { ArrowRight, Sparkles, Heart, Star } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import FoxMotif from "@/components/site/FoxMotif";
import PhoneMockup from "@/components/site/PhoneMockup";
import Reveal from "@/components/site/Reveal";
import FloatingBanners from "@/components/site/FloatingBanners";
import { useContent } from "@/contexts/ContentContext";

const smoothScroll = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Hero = ({ content }) => {
  const reduce = useReducedMotion();
  const { loading, hasCached } = useContent();
  const isStillLoading = loading && !hasCached;
  const c = content || {};

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative overflow-hidden pt-12 sm:pt-16 lg:pt-20 pb-16 sm:pb-20"
    >
      <div className="pointer-events-none absolute -top-32 -left-32 h-[520px] w-[520px] pf-hero-blob" aria-hidden="true" />
      <div className="pointer-events-none absolute inset-0 pf-grid-bg opacity-[0.35]" aria-hidden="true" />
      <div className="pointer-events-none absolute inset-0 pf-noise" aria-hidden="true" />

      <div className="pf-container relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-6">
            <Reveal>
              <div className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--border))] bg-white pl-1 pr-3 py-1 pf-shadow-sm">
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#F35A1F]">
                  <Sparkles className="h-3.5 w-3.5 text-white" />
                </span>
                <span className="pf-mono text-[11px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                  {c.badge_label}
                </span>
              </div>
            </Reveal>

            <Reveal delay={0.06}>
              <h1
                data-testid="hero-headline"
                className="mt-5 pf-display text-[44px] leading-[1.02] sm:text-[60px] lg:text-[76px] tracking-tight text-[hsl(var(--foreground))]"
              >
                {c.headline_line1}
                <br />
                <span className="relative inline-block">
                  <span className="relative z-10 text-[#F35A1F]">{c.headline_line2}</span>
                  {!reduce && (
                    <motion.span
                      aria-hidden
                      initial={{ scaleX: 0 }}
                      animate={{ scaleX: 1 }}
                      transition={{ delay: 0.6, duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
                      style={{ originX: 0 }}
                      className="absolute -bottom-1 left-0 right-0 h-2 rounded-full bg-[#FFEEE6]"
                    />
                  )}
                </span>
              </h1>
            </Reveal>

            <Reveal delay={0.12}>
              <p
                data-testid="hero-subheadline"
                className="mt-5 max-w-xl text-base sm:text-lg text-[hsl(var(--muted-foreground))] leading-relaxed"
              >
                {c.subheadline}
              </p>
            </Reveal>

            <Reveal delay={0.18}>
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Button
                  data-testid="hero-primary-cta"
                  onClick={() => smoothScroll("apps")}
                  className="group h-12 rounded-xl bg-[#F35A1F] px-5 text-base text-white hover:bg-[#D94A14] focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2"
                >
                  {c.primary_cta_label}
                  <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-0.5" />
                </Button>
                <Button
                  variant="outline"
                  data-testid="hero-secondary-cta"
                  onClick={() => smoothScroll("portfolio")}
                  className="h-12 rounded-xl border-[hsl(var(--border))] bg-white px-5 text-base text-[hsl(var(--foreground))] hover:bg-[hsl(var(--accent))]"
                >
                  {c.secondary_cta_label}
                </Button>
              </div>
            </Reveal>

            <Reveal delay={0.24}>
              {(() => {
                const items = [];
                if (c.rating_visible !== false && (c.rating_value || c.rating_label)) {
                  items.push(
                    <div
                      key="rating"
                      data-testid="hero-trust-rating"
                      className="flex items-center gap-2"
                    >
                      <Star className="h-4 w-4 text-[#F35A1F] fill-[#F35A1F]" />
                      <span>
                        {c.rating_value}
                        {c.rating_value && c.rating_label ? " " : ""}
                        {c.rating_label}
                      </span>
                    </div>
                  );
                }
                if (c.tagline_visible !== false && c.tagline_text) {
                  items.push(
                    <div
                      key="tagline"
                      data-testid="hero-trust-tagline"
                      className="flex items-center gap-2"
                    >
                      <Heart className="h-4 w-4 text-[#F35A1F]" />
                      <span>{c.tagline_text}</span>
                    </div>
                  );
                }
                if (c.new_badge_visible !== false && (c.new_badge_label || c.new_badge_text)) {
                  items.push(
                    <div
                      key="new"
                      data-testid="hero-trust-new"
                      className="flex items-center gap-2"
                    >
                      {c.new_badge_label && (
                        <Badge className="rounded-full bg-[#FFEEE6] text-[#D94A14] hover:bg-[#FFEEE6] border-0 px-2.5 py-0.5 text-[11px] font-semibold">
                          {c.new_badge_label}
                        </Badge>
                      )}
                      {c.new_badge_text && <span>{c.new_badge_text}</span>}
                    </div>
                  );
                }
                if (items.length === 0) return null;
                const withSeparators = [];
                items.forEach((node, i) => {
                  if (i > 0) {
                    withSeparators.push(
                      <div
                        key={`sep-${i}`}
                        className="hidden sm:block h-4 w-px bg-[hsl(var(--border))]"
                        aria-hidden="true"
                      />
                    );
                  }
                  withSeparators.push(node);
                });
                return (
                  <div
                    data-testid="hero-trust-row"
                    className="mt-9 flex flex-wrap items-center gap-6 text-sm text-[hsl(var(--muted-foreground))]"
                  >
                    {withSeparators}
                  </div>
                );
              })()}
            </Reveal>
          </div>

          <div className="lg:col-span-6 relative" data-testid="hero-phone-wrap">
            <div className="pointer-events-none absolute -top-6 left-2 opacity-[0.16] sm:opacity-[0.20]" aria-hidden="true">
              <FoxMotif size={260} testId="hero-fox-motif" />
            </div>

            <FloatingBanners
              banners={c.floating_banners}
              testIdPrefix="hero-floating-banner"
            />

            <div className="relative flex justify-center lg:justify-end">
              <PhoneMockup variant="hero" imageId={c.phone_screen_image_id} loading={isStillLoading} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
